import {
  type CommunityChannelData,
  type PostUpdateUserGroupMembershipDto,
} from '@ccm-innovation/member-center-bff-sdk-axios';
import { cx } from 'cva';
import _ from 'lodash';
import { type FunctionComponent, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { assert } from 'ts-essentials';
import { AlertDialog } from '../../atoms/AlertDialog/AlertDialog';
import { Button } from '../../atoms/Button/Button';
import { updateUserGroupMembershipSlice } from '../../core/async-action/update-user-group-membership/update-user-group-membership.slice';
import { useTranslation } from '../../i18n/resources';
import styles from './GroupManagementAlertDialog.module.css';

export type GroupManagementAlertDialogProps = {
  memberChannelData: CommunityChannelData[];
  onboarding: boolean;
  onClose: ({ userClosed }: { userClosed: boolean }) => void;
};

export const GroupManagementAlertDialog: FunctionComponent<
  GroupManagementAlertDialogProps
> = ({ memberChannelData, onboarding, onClose }) => {
  assert(
    memberChannelData,
    'GroupManagementAlertDialog -- Missing Member Channel Data',
  );

  const dispatch = useDispatch();
  const { renderTrans, t } = useTranslation();
  const [updatedChannelData, setUpdatedChannelData] =
    useState<CommunityChannelData[]>(memberChannelData);

  const canSubmitUpdate = useMemo(() => {
    return !_.isEqual(memberChannelData, updatedChannelData);
  }, [memberChannelData, updatedChannelData]);

  const toggleMembershipByChannelId = (channelId: string): void => {
    setUpdatedChannelData((channels) => {
      const next = channels.map((channel) => {
        if (channel.channelId === channelId) {
          return { ...channel, isMember: !channel.isMember };
        }
        return channel;
      });

      return next;
    });
  };

  const renderChannelButtons = (): React.ReactElement[] => {
    return updatedChannelData.map((channel) => {
      const { channelId, channelName, isMember } = channel;
      return (
        <Button
          className={cx(styles.channelButton, !isMember && styles.notJoined)}
          intent={isMember ? 'primary' : 'secondary'}
          key={channelId}
          onClick={() => {
            toggleMembershipByChannelId(channelId);
          }}
        >
          {channelName}
        </Button>
      );
    });
  };

  const handleApplyChanges = (): void => {
    const updateUserGroupMembershipPayload: PostUpdateUserGroupMembershipDto = {
      communityGroupsToAdd: [],
      communityGroupsToRemove: [],
    };

    for (const [idx, updatedChannel] of updatedChannelData.entries()) {
      if (!memberChannelData[idx]?.isMember && updatedChannel.isMember) {
        updateUserGroupMembershipPayload.communityGroupsToAdd.push({
          channelId: updatedChannel.channelId,
          channelType: updatedChannel.channelType,
        });
      } else if (memberChannelData[idx]?.isMember && !updatedChannel.isMember) {
        updateUserGroupMembershipPayload.communityGroupsToRemove.push({
          channelId: updatedChannel.channelId,
          channelType: updatedChannel.channelType,
        });
      }
    }

    dispatch(
      updateUserGroupMembershipSlice.actions.updateUserGroupMembershipRequested(
        updateUserGroupMembershipPayload,
      ),
    );
    onClose({ userClosed: false });
  };

  return (
    <AlertDialog
      footer={
        <div className={styles.actionButtons}>
          <Button
            intent="secondary"
            onClick={() => {
              onClose({ userClosed: true });
            }}
          >
            {onboarding
              ? t('groupManagement', 'skip_for_now')
              : t('groupManagement', 'cancel')}
          </Button>
          <Button disabled={!canSubmitUpdate} onClick={handleApplyChanges}>
            {t('groupManagement', 'apply')}
          </Button>
        </div>
      }
      onClose={() => {
        onClose({ userClosed: true });
      }}
      open
      padding="medium"
      size="medium"
      title={
        onboarding
          ? t('groupManagement', 'choose_your_community_groups')
          : t('groupManagement', 'manage_community_groups')
      }
    >
      <div className={styles.descriptionContainer}>
        <div className={styles.groupManagementDetails}>
          {onboarding
            ? renderTrans('groupManagement', 'onboarding_details')
            : t('groupManagement', 'manage_details')}
        </div>
        <div className={styles.channelButtonsContainer}>
          {renderChannelButtons()}
        </div>
      </div>
    </AlertDialog>
  );
};
